/**
 * SEO head for the browse containers and their `?preset=` category pages.
 *
 * Core's `ContentMetadataTags` canonicalises every one of these URLs to the
 * bare container — `/documents?preset=archives-browse` declared itself a copy
 * of `/documents`. That instruction is obeyed: a self-excluded page is
 * consolidated away, so the 17 category pages could never be indexed no matter
 * how well they were linked. They are not duplicates; each is a different
 * result set with a different name, and "Archived Standards" is exactly the
 * kind of query a category page should win.
 *
 * So each category page gets a self-referencing canonical, its own title and
 * its own description. Paginated pages likewise: `?page=2` is self-canonical
 * (Google's guidance since it retired rel=next/prev handling) so the links it
 * carries into the archive are honoured rather than discarded.
 *
 * The canonical is built from `preset` and the page number ALONE, never from
 * the rest of the query string — facet and sort state must not mint indexable
 * near-duplicates of a page that already exists.
 *
 * A category that currently matches nothing is `noindex,follow`: four presets
 * are relative-date windows ("Recently Canceled" = updated within 90 days)
 * over a frozen archive and match nothing today, and an empty page is what
 * Google reads as thin content or a soft 404. `follow` keeps its navigation
 * live, and because the rule is driven by the measured total it lifts by
 * itself the day the archive starts taking new documents.
 *
 * The threshold is the preset's `minTotal`, defaulting to 1, so that rule is
 * unchanged for every preset that does not set one. The directives series
 * presets set 10: they exist to shorten a 19-hop crawl path, which a link
 * achieves on its own, and the thin tail of that taxonomy has no business in
 * the sitemap of the archive that was just deindexed for thin content.
 *
 * Helmet resolves `<link rel="canonical">` by last declaration, and this block
 * renders below `ContentMetadataTags` in the tree, so this wins without core
 * being patched.
 */
import React from 'react';
import { Helmet } from '@plone/volto/helpers';

import { absoluteUrl } from './browseState';
import { PUBLISHER_NAME } from '../../siteConfig/branding';

/** What this family's rows actually are, for a description that reads true. */
const FAMILY_NOUN = {
  documents: 'documents',
  delegations: 'delegations',
  glossary: 'glossary entries',
  people: 'people',
};

/**
 * The same nouns for a count of exactly one. A map rather than a trailing "s"
 * because `people` and `glossary entries` do not pluralise that way, and
 * because a count of one is now common: the directives series presets put
 * several single-document categories on the site.
 */
const FAMILY_NOUN_ONE = {
  documents: 'document',
  delegations: 'delegation',
  glossary: 'glossary entry',
  people: 'person',
};

/**
 * The name for the UNFILTERED container page — the one with no `?preset=` and
 * no pagination.
 *
 * This exists because those pages used to fall through to core's
 * `ContentMetadataTags`, which emits the raw Plone folder title and nothing
 * else. `/documents` is the most-viewed page on the property and its `<title>`
 * was the single word **"Documents"**, with an EMPTY meta description — and
 * all three archives emitted the identical string, so the three most important
 * hub pages in the estate were indistinguishable to a search engine and to
 * anyone reading a result. `/glossary` had the same problem. See BL-0043.
 *
 * The folder title is right for a breadcrumb and wrong for a title tag: a
 * title tag is read out of context, in a list of ten competing results, where
 * "Documents" says nothing at all.
 */
const BARE_NAME = {
  documents: 'All documents',
  delegations: 'All delegations',
  glossary: 'Glossary',
  people: 'People',
};

/**
 * How you actually use each container, one sentence, for the unfiltered page's
 * description. Family-specific on purpose: the first draft of this reused the
 * documents wording everywhere and told readers of `/glossary` they could
 * filter glossary entries "by type, series, status and date", which is not
 * true of that page. A description that promises a control the page does not
 * have is worse than a generic one.
 */
const BARE_TAIL = {
  documents:
    'Search by document number, or filter by type, series, status and date.',
  delegations:
    'Search by number, or filter by issuing authority, status and date.',
  glossary: 'Search for a term or acronym, or browse the full list.',
  people: 'Browse the people named across these documents.',
};

/**
 * The count clause of a browse description: "705 documents", "no documents",
 * "1 document", or the bare noun when the total has not been measured yet.
 * Exported for the same reason as `browseRobots` below.
 */
export const countPhrase = (total, family) => {
  const noun = FAMILY_NOUN[family] || 'records';
  if (typeof total !== 'number') return noun;
  if (total === 0) return `no ${noun}`;
  if (total === 1) return `1 ${FAMILY_NOUN_ONE[family] || 'record'}`;
  return `${total.toLocaleString('en-US')} ${noun}`;
};

/**
 * The robots value for a browse surface, exported as a plain function so it
 * can be asserted without a router and a store (same reason as
 * SEO/NoIndexRoutes).
 *
 * `total` of `undefined` means "not measured yet" (no SSR seed, first client
 * render) and is never treated as empty. `minTotal` defaults to 1, which is
 * the original rule: only a measured zero is an empty category.
 */
export const browseRobots = (total, minTotal) =>
  typeof total === 'number' && total < (minTotal ?? 1)
    ? 'noindex,follow'
    : 'index,follow';

/**
 * The total of a `querystringsearch` subrequest IF it is a measurement, else
 * `undefined` ("not measured", which `browseRobots` and `countPhrase` already
 * treat as unknown rather than empty).
 *
 * Core writes `total: 0` both for a search that matched nothing and for a
 * request that FAILED, and for a request still pending with nothing before it.
 * Only the first is a zero. Reading the other two as zero is what made every
 * hub and `?preset=` page render `noindex,follow` for rendering crawlers from
 * 2026-09-20, when robots.txt stopped them calling `/++api++/` and the
 * hydration refetch failed (see reducers/querystringsearch). So: an `error`
 * is never a count, and a total counts only once a response has landed
 * (`loaded`) or the previous response's rows are still on screen.
 */
export const measuredTotal = (subrequest) =>
  subrequest &&
  !subrequest.error &&
  (subrequest.loaded || subrequest.items?.length)
    ? subrequest.total
    : undefined;

const BrowseSEO = ({
  site,
  family,
  preset,
  content,
  pathname,
  pageKey,
  currentPage,
  total,
  bSize,
}) => {
  const paged = currentPage > 1;
  // The unfiltered container page. It used to return null here and inherit
  // core's bare folder title with no description at all (BL-0043); it is now
  // handled like every other browse surface, with the same measured count.
  const bare = !preset && !paged;

  const params = [];
  if (preset?.id) params.push(`preset=${encodeURIComponent(preset.id)}`);
  if (paged) params.push(`${pageKey}=${currentPage}`);
  const href = absoluteUrl(
    site,
    params.length ? `${pathname}?${params.join('&')}` : pathname,
  );

  const totalPages = total ? Math.ceil(total / (bSize || 25)) : 0;
  const ofPages = totalPages ? ` of ${totalPages}` : '';
  const name =
    preset?.label ||
    (bare ? BARE_NAME[family] : undefined) ||
    content?.title ||
    'Browse';
  // `seoName` in the TITLE (it names the corpus for a searcher) but plain
  // `title` in the descriptions below, where the sentence already supplies the
  // word "archive" and `seoName` would read as "... Archive archive".
  const title = paged
    ? `${name} — page ${currentPage}${ofPages} | ${site?.seoName}`
    : `${name} | ${site?.seoName}`;

  const counted = countPhrase(total, family);
  const description = paged
    ? `${name} in the ${site?.title} archive — ${counted}, page ${currentPage}${ofPages}.`
    : bare
      ? `${counted} in the ${site?.title} archive. ${
          BARE_TAIL[family] || 'Search or filter to narrow the list.'
        }`
      : `${name} in the ${site?.title} archive — ${counted}.`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={browseRobots(total, preset?.minTotal)} />
      <link rel="canonical" href={href} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={name} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={PUBLISHER_NAME} />
      <meta property="og:url" content={href} />
    </Helmet>
  );
};

export default BrowseSEO;
